import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../config/api.config';
import './SchoolManagement.css';
import defaultSchoolLogo from '../../../img/default-school.png';



const SchoolManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentSchool, setCurrentSchool] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    imageUrl: '',
    active: true,
    schoolType: 'classroom', // Default to Classroom Success Plan
    adminEmail: '',
    adminPassword: '',
    teacherEmail: '',
    teacherPassword: '',
    teacherName: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showTrials, setShowTrials] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all schools
  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async (includeInactive = false) => {
    try {
      setLoading(true);
      setShowInactive(includeInactive);
      const endpoint = includeInactive 
        ? `${API_ENDPOINTS.OWNER.SCHOOLS}?includeInactive=true`
        : API_ENDPOINTS.OWNER.SCHOOLS;
        
      const response = await axios.get(
        getApiUrl(endpoint),
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        console.log('Schools data:', response.data);
        setSchools(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load schools');
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err.response?.data?.message || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'code') {
      // Convert to uppercase and remove non-alphanumeric characters
      const formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
      setFormData({ ...formData, [name]: formattedValue });
    } else if (name === 'active') {
      setFormData({ ...formData, [name]: e.target.checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is a JPEG or PNG
      if (!file.type.match('image/jpeg|image/png')) {
        setFormErrors({...formErrors, logo: 'Logo must be a JPEG or PNG file'});
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      if (formErrors.logo) {
        const { logo, ...rest } = formErrors;
        setFormErrors(rest);
      }
    } else {
      setLogoFile(null);
      setLogoPreview('');
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'School name is required';
    }
    
    if (showAddForm && (!formData.code || formData.code.length !== 16)) {
      errors.code = 'School code must be exactly 16 characters';
    }
    
    if (showAddForm && formData.schoolType === 'classroom') {
      if (!formData.adminEmail || !formData.adminEmail.trim()) {
        errors.adminEmail = 'Admin email is required for Classroom Success Plan';
      } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
        errors.adminEmail = 'Please enter a valid email address';
      }
      
      if (!formData.adminPassword || formData.adminPassword.length < 8) {
        errors.adminPassword = 'Admin password must be at least 8 characters';
      }
    }

    if (showAddForm && formData.schoolType === 'personal') {
      if (!formData.teacherName || !formData.teacherName.trim()) {
        errors.teacherName = 'Teacher name is required for Personal Learning Plan';
      }
      
      if (!formData.teacherEmail || !formData.teacherEmail.trim()) {
        errors.teacherEmail = 'Teacher email is required for Personal Learning Plan';
      } else if (!/\S+@\S+\.\S+/.test(formData.teacherEmail)) {
        errors.teacherEmail = 'Please enter a valid email address';
      }
      
      if (!formData.teacherPassword || formData.teacherPassword.length < 8) {
        errors.teacherPassword = 'Teacher password must be at least 8 characters';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    
    const formData = new FormData();
    formData.append('file', logoFile);
    
    try {
      const response = await axios.post(
        getApiUrl(API_ENDPOINTS.UPLOAD),
        formData,
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (response.data.success) {
        return response.data.fileUrl;
      }
      return null;
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Failed to upload logo. Please try again.');
      return null;
    }
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Upload logo if one was selected
      let logoUrl = null;
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl) {
          setLoading(false);
          return;
        }
      }
      
      // Step 1: First create the school
      const schoolData = {
        code: formData.code,
        name: formData.name,
        imageUrl: logoUrl || formData.imageUrl,
        active: formData.active
      };
      
      console.log('Creating school with data:', schoolData);
      
      const schoolResponse = await axios.post(
        getApiUrl(API_ENDPOINTS.OWNER.SCHOOLS),
        schoolData,
        { headers: getAuthHeaders() }
      );

      if (!schoolResponse.data.success) {
        setError(schoolResponse.data.message || 'Failed to add school');
        setLoading(false);
        return;
      }
      
      // Step 2: Create appropriate user account based on school type
      if (formData.schoolType === 'classroom') {
        try {
          // Create the admin user for this school with payment permissions
          const adminData = {
            name: `${formData.name} Admin`,
            email: formData.adminEmail,
            password: formData.adminPassword,
            schoolCode: formData.code,
            schoolType: 'classroom',
            permissions: ['view_payments', 'manage_payments'] // Only classroom admins can manage payments
          };
          
          console.log('Creating admin user with data:', adminData);
          
          const adminResponse = await axios.post(
            getApiUrl(API_ENDPOINTS.OWNER.CREATE_ADMIN),
            adminData,
            { headers: getAuthHeaders() }
          );
          
          if (adminResponse.data.success) {
            // Create a default teacher account for classroom plan
            const teacherData = {
              name: 'Default Teacher',
              email: `teacher.${formData.code.toLowerCase()}@stemathome.org`,
              password: 'DefaultPass123', // This should be changed on first login
              schoolCode: formData.code,
              role: 'teacher',
              schoolType: 'classroom',
              permissions: [] // Classroom teachers don't get payment permissions
            };

            try {
              const teacherResponse = await axios.post(
                getApiUrl(API_ENDPOINTS.OWNER.CREATE_TEACHER),
                teacherData,
                { headers: getAuthHeaders() }
              );

              if (teacherResponse.data.success) {
                setSuccessMessage('School, admin account, and default teacher account created successfully');
              } else {
                setError(`School and admin created but failed to create teacher: ${teacherResponse.data.message || 'Unknown error'}`);
              }
            } catch (teacherErr) {
              console.error('Error creating teacher:', teacherErr);
              setError(`School and admin created but failed to create teacher: ${teacherErr.response?.data?.message || teacherErr.message}`);
            }
          } else {
            setError(`School created but failed to create admin: ${adminResponse.data.message || 'Unknown error'}`);
          }
        } catch (adminErr) {
          console.error('Error creating admin user:', adminErr);
          setError(`School created but failed to create admin: ${adminErr.response?.data?.message || adminErr.message}`);
        }
      } else if (formData.schoolType === 'personal') {
        try {
          // Create the teacher user for this school with payment permissions
          const teacherData = {
            name: formData.teacherName,
            email: formData.teacherEmail,
            password: formData.teacherPassword,
            schoolCode: formData.code,
            role: 'teacher',
            schoolType: 'personal',
            permissions: ['view_payments', 'manage_payments'] // Personal Learning Plan teachers can manage payments
          };
          
          console.log('Creating teacher user with data:', teacherData);
          
          // Use the owner-specific endpoint for creating teacher users
          const teacherResponse = await axios.post(
            getApiUrl(API_ENDPOINTS.OWNER.CREATE_TEACHER),
            teacherData,
            { headers: getAuthHeaders() }
          );
          
          if (teacherResponse.data.success) {
            setSuccessMessage('School and teacher account created successfully');
          } else {
            setError(`School created but failed to create teacher: ${teacherResponse.data.message || 'Unknown error'}`);
          }
        } catch (teacherErr) {
          console.error('Error creating teacher user:', teacherErr);
          setError(`School created but failed to create teacher: ${teacherErr.response?.data?.message || teacherErr.message}`);
        }
      } else {
        setSuccessMessage('School created successfully');
      }
      
      fetchSchools();
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding school:', err);
      setError(err.response?.data?.message || 'Failed to add school');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchool = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Upload logo if one was selected
      let logoUrl = null;
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl) {
          setLoading(false);
          return;
        }
      }
      
      const schoolData = {
        ...formData,
        imageUrl: logoUrl || formData.imageUrl
      };
      
      const response = await axios.put(
        getApiUrl(API_ENDPOINTS.OWNER.SCHOOL_DETAILS(currentSchool.code)),
        schoolData,
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setSuccessMessage('School updated successfully');
        fetchSchools();
        resetForm();
        setShowEditForm(false);
      } else {
        setError(response.data.message || 'Failed to update school');
      }
    } catch (err) {
      console.error('Error updating school:', err);
      setError(err.response?.data?.message || 'Failed to update school');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchool = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(
        getApiUrl(API_ENDPOINTS.OWNER.SCHOOL_DETAILS(currentSchool.code)),
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setSuccessMessage('School deleted successfully');
        fetchSchools();
        setShowDeleteConfirm(false);
        setCurrentSchool(null);
      } else {
        setError(response.data.message || 'Failed to delete school');
      }
    } catch (err) {
      console.error('Error deleting school:', err);
      if (err.response?.status === 400 && err.response?.data?.message?.includes('users')) {
        setError('Cannot delete a school with associated users. Please deactivate it instead.');
      } else {
        setError(err.response?.data?.message || 'Failed to delete school');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateSchool = async (school) => {
    try {
      setLoading(true);
      const response = await axios.put(
        getApiUrl(API_ENDPOINTS.OWNER.SCHOOL_DETAILS(school.code)),
        { active: false },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setSuccessMessage('School deactivated successfully');
        fetchSchools(true); // Include inactive schools in the next fetch
      } else {
        setError(response.data.message || 'Failed to deactivate school');
      }
    } catch (err) {
      console.error('Error deactivating school:', err);
      setError(err.response?.data?.message || 'Failed to deactivate school');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      imageUrl: '',
      active: true,
      schoolType: 'classroom',
      adminEmail: '',
      adminPassword: '',
      teacherEmail: '',
      teacherPassword: '',
      teacherName: ''
    });
    setLogoFile(null);
    setLogoPreview('');
    setFormErrors({});
  };

  const openAddForm = () => {
    resetForm();
    setShowAddForm(true);
    setShowEditForm(false);
    setShowDeleteConfirm(false);
    
    // Add scroll functionality after a short delay to ensure the form is rendered
    setTimeout(() => {
      const formContainer = document.querySelector('.form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const openEditForm = (school) => {
    setCurrentSchool(school);
    setFormData({
      code: school.code,
      name: school.name,
      imageUrl: school.imageUrl || '',
      active: school.active !== false
    });
    
    // Reset file selection
    setLogoFile(null);
    setLogoPreview(school.imageUrl || '');
    
    setShowEditForm(true);
    setShowAddForm(false);
    setShowDeleteConfirm(false);
    
    // Add scroll functionality after a short delay to ensure the form is rendered
    setTimeout(() => {
      const formContainer = document.querySelector('.form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const openDeleteConfirm = (school) => {
    setCurrentSchool(school);
    setShowDeleteConfirm(true);
    setShowAddForm(false);
    setShowEditForm(false);
    
    // Add scroll functionality after a short delay to ensure the dialog is rendered
    setTimeout(() => {
      const confirmDialog = document.querySelector('.confirm-dialog-content');
      if (confirmDialog) {
        confirmDialog.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const closeAllForms = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setShowDeleteConfirm(false);
    resetForm();
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Filter schools based on search term and toggle settings
  const filteredSchools = schools.filter(school => {
    // If search term is empty, return all schools
    const matchesSearch = !searchTerm.trim() || 
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by trial status
    const matchesTrial = showTrials || !school.isTrial;
    
    return matchesSearch && matchesTrial;
  });

  // Separate schools into regular and trial categories
  const regularSchools = filteredSchools.filter(school => !school.isTrial);
  const trialSchools = filteredSchools.filter(school => school.isTrial);

  if (loading && schools.length === 0) {
    return (
      <div className="school-management-container">
        <div className="loading">Loading schools...</div>
      </div>
    );
  }

  return (
    <div className="school-management-container">
      <div className="page-header">
        <h1>School Management</h1>
      </div>

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
          <button className="close-btn" onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="close-btn" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && currentSchool && (
        <div className="confirm-dialog">
          <div className="confirm-dialog-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete <strong>{currentSchool.name}</strong>?</p>
            <p className="text-danger">Warning: This action cannot be undone if the school has no users.</p>
            <p className="text-warning">If the school has users, it will be deactivated instead of deleted.</p>
            
            <div className="confirm-buttons">
              <button className="btn btn-danger" onClick={handleDeleteSchool}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={closeAllForms}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Schools section with integrated search and controls */}
      <div className="schools-table-container">
        <div className="schools-header-wrapper">
          <div className="schools-header">
            <h2>Schools ({schools.length})</h2>
          </div>
          
          <div className="schools-actions">
            <div className="search-box">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search schools..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="bi bi-search search-icon"></i>
            </div>
            
            <div className="filter-options">
              <div className="form-check form-switch">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="showInactiveSwitch"
                  onChange={(e) => fetchSchools(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showInactiveSwitch">
                  Show Inactive Schools
                </label>
              </div>
              <div className="form-check form-switch ms-3">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="showTrialSwitch"
                  checked={showTrials}
                  onChange={(e) => setShowTrials(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showTrialSwitch">
                  Show Trial Accounts
                </label>
              </div>
            </div>
            
            <button 
              className="btn btn-gold" 
              onClick={openAddForm}
              disabled={showAddForm}
            >
              <i className="bi bi-plus-circle"></i> Add New School
            </button>
          </div>
        </div>
        
        {/* Add School Form */}
        {showAddForm && (
          <div className="form-container">
            <h2>Add New School</h2>
            <form onSubmit={handleAddSchool}>
              <div className="form-group">
                <label htmlFor="schoolType">School Type*</label>
                <select
                  id="schoolType"
                  name="schoolType"
                  value={formData.schoolType}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="classroom">Classroom Success Plan</option>
                  <option value="personal">Personal Learning Plan</option>
                </select>
                <small className="form-text text-muted">
                  Classroom Success Plan requires an admin account to be created.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="code">School Code (16 characters)*</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className={`form-control ${formErrors.code ? 'is-invalid' : ''}`}
                  placeholder="SCHOOLCODE123456"
                  maxLength={16}
                  required
                />
                {formErrors.code && <div className="invalid-feedback">{formErrors.code}</div>}
                <small className="form-text text-muted">
                  Must be exactly 16 characters, uppercase letters and numbers only.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="name">School Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                  placeholder="Enter school name"
                  required
                />
                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="logoFile">School Logo</label>
                <input
                  type="file"
                  id="logoFile"
                  name="logoFile"
                  onChange={handleFileChange}
                  className={`form-control ${formErrors.logo ? 'is-invalid' : ''}`}
                  accept="image/jpeg, image/png"
                />
                {formErrors.logo && <div className="invalid-feedback">{formErrors.logo}</div>}
                <small className="form-text text-muted">
                  Optional. Only JPEG or PNG files are accepted. If left empty, default logo will be used.
                </small>
                
                {logoPreview && (
                  <div className="logo-preview mt-2">
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      className="img-thumbnail" 
                      style={{ maxHeight: '100px' }} 
                    />
                  </div>
                )}
              </div>

              {formData.schoolType === 'classroom' && (
                <>
                  <h3 className="admin-account-section">Admin Account</h3>
                  <div className="form-group">
                    <label htmlFor="adminEmail">Admin Email*</label>
                    <input
                      type="email"
                      id="adminEmail"
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleInputChange}
                      className={`form-control ${formErrors.adminEmail ? 'is-invalid' : ''}`}
                      placeholder="admin@school.com"
                      required={formData.schoolType === 'classroom'}
                    />
                    {formErrors.adminEmail && <div className="invalid-feedback">{formErrors.adminEmail}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="adminPassword">Admin Password*</label>
                    <input
                      type="password"
                      id="adminPassword"
                      name="adminPassword"
                      value={formData.adminPassword}
                      onChange={handleInputChange}
                      className={`form-control ${formErrors.adminPassword ? 'is-invalid' : ''}`}
                      placeholder="Password (minimum 8 characters)"
                      required={formData.schoolType === 'classroom'}
                    />
                    {formErrors.adminPassword && <div className="invalid-feedback">{formErrors.adminPassword}</div>}
                    <small className="form-text text-muted">
                      Password must be at least 8 characters.
                    </small>
                  </div>
                </>
              )}

              {formData.schoolType === 'personal' && (
                <>
                  <h3 className="admin-account-section">Teacher Account</h3>
                  <div className="form-group">
                    <label htmlFor="teacherName">Teacher Name*</label>
                    <input
                      type="text"
                      id="teacherName"
                      name="teacherName"
                      value={formData.teacherName}
                      onChange={handleInputChange}
                      className={`form-control ${formErrors.teacherName ? 'is-invalid' : ''}`}
                      placeholder="John Doe"
                      required={formData.schoolType === 'personal'}
                    />
                    {formErrors.teacherName && <div className="invalid-feedback">{formErrors.teacherName}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="teacherEmail">Teacher Email*</label>
                    <input
                      type="email"
                      id="teacherEmail"
                      name="teacherEmail"
                      value={formData.teacherEmail}
                      onChange={handleInputChange}
                      className={`form-control ${formErrors.teacherEmail ? 'is-invalid' : ''}`}
                      placeholder="teacher@school.com"
                      required={formData.schoolType === 'personal'}
                    />
                    {formErrors.teacherEmail && <div className="invalid-feedback">{formErrors.teacherEmail}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="teacherPassword">Teacher Password*</label>
                    <input
                      type="password"
                      id="teacherPassword"
                      name="teacherPassword"
                      value={formData.teacherPassword}
                      onChange={handleInputChange}
                      className={`form-control ${formErrors.teacherPassword ? 'is-invalid' : ''}`}
                      placeholder="Password (minimum 8 characters)"
                      required={formData.schoolType === 'personal'}
                    />
                    {formErrors.teacherPassword && <div className="invalid-feedback">{formErrors.teacherPassword}</div>}
                    <small className="form-text text-muted">
                      Password must be at least 8 characters.
                    </small>
                  </div>
                </>
              )}

              <div className="form-check">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="form-check-input"
                />
                <label className="form-check-label" htmlFor="active">
                  Active
                </label>
              </div>

              <div className="form-buttons">
                <button type="submit" className="btn btn-success">
                  Add School
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeAllForms}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Edit School Form */}
        {showEditForm && currentSchool && (
          <div className="form-container">
            <h2>Edit School</h2>
            <form onSubmit={handleEditSchool}>
              <div className="form-group">
                <label htmlFor="codeDisplay">School Code</label>
                <input
                  type="text"
                  id="codeDisplay"
                  value={formData.code}
                  className="form-control"
                  disabled
                />
                <small className="form-text text-muted">
                  School code cannot be changed.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="name">School Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                  placeholder="Enter school name"
                  required
                />
                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="logoFile">School Logo</label>
                <input
                  type="file"
                  id="logoFile"
                  name="logoFile"
                  onChange={handleFileChange}
                  className={`form-control ${formErrors.logo ? 'is-invalid' : ''}`}
                  accept="image/jpeg, image/png"
                />
                {formErrors.logo && <div className="invalid-feedback">{formErrors.logo}</div>}
                <small className="form-text text-muted">
                  Optional. JPEG or PNG formats only.
                </small>
                
                {logoPreview && (
                  <div className="logo-preview mt-2">
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      className="img-thumbnail" 
                      style={{ maxHeight: '100px' }} 
                    />
                  </div>
                )}
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="editActive"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="form-check-input"
                />
                <label className="form-check-label" htmlFor="editActive">
                  Active
                </label>
              </div>

              <div className="form-buttons">
                <button type="submit" className="btn btn-success">
                  Update School
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeAllForms}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Schools Grid */}
        {schools.length === 0 ? (
          <div className="no-schools-message">
            <p>No schools have been added yet. Click on 'Add New School' to create a new school.</p>
          </div>
        ) : filteredSchools.length === 0 ? (
          <div className="no-schools-message">
            <p>No schools match your search criteria. Try a different search term.</p>
          </div>
        ) : (
          <>
            {/* Regular Schools Section */}
            {regularSchools.length > 0 && (
              <div className="school-section">
                <h2 className="section-title">Regular Schools ({regularSchools.length})</h2>
                <div className="schools-grid">
                  {regularSchools.map(school => (
                    <div 
                      className={`school-card ${!school.active ? 'inactive-card' : ''}`} 
                      key={school.code}
                    >
                      <div className="school-card-header">
                        <img 
                          src={school.imageUrl || defaultSchoolLogo} 
                          alt={`${school.name} logo`}
                          className="school-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultSchoolLogo;
                          }}
                        />
                        
                        <div className="status-badge-container">
                          <span className={`status-badge ${school.active ? 'active' : 'inactive'}`}>
                            {school.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="school-card-body">
                        <h3 className="school-name">{school.name}</h3>
                        
                        <div className="school-detail">
                          <span className="detail-label">Code:</span>
                          <span className="school-code">{school.code}</span>
                        </div>
                        
                        <div className="school-detail">
                          <span className="detail-label">Created:</span>
                          <span className="school-date">
                            {new Date(school.createdAt).toLocaleDateString()} {new Date(school.createdAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                      </div>
                      
                      <div className="school-card-footer">
                        <div className="action-buttons">
                          <button
                            className="btn btn-gold btn-sm"
                            onClick={() => openEditForm(school)}
                          >
                            Edit
                          </button>
                          {school.active ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDeactivateSchool(school)}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => openDeleteConfirm(school)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trial Schools Section */}
            {trialSchools.length > 0 && (
              <div className="school-section">
                <h2 className="section-title">Trial Accounts ({trialSchools.length})</h2>
                <div className="schools-grid">
                  {trialSchools.map(school => (
                    <div 
                      className={`school-card trial-card ${!school.active ? 'inactive-card' : ''}`} 
                      key={school.code}
                    >
                      <div className="school-card-header">
                        <img 
                          src={school.imageUrl || defaultSchoolLogo} 
                          alt={`${school.name} logo`}
                          className="school-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultSchoolLogo;
                          }}
                        />
                        
                        <div className="status-badge-container">
                          <span className={`status-badge ${school.active ? 'active' : 'inactive'}`}>
                            {school.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="status-badge trial">Trial</span>
                        </div>
                      </div>
                      
                      <div className="school-card-body">
                        <h3 className="school-name">{school.name}</h3>
                        
                        <div className="school-detail">
                          <span className="detail-label">Code:</span>
                          <span className="school-code">{school.code}</span>
                        </div>
                        
                        <div className="school-detail">
                          <span className="detail-label">Created:</span>
                          <span className="school-date">
                            {new Date(school.createdAt).toLocaleDateString()} {new Date(school.createdAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        
                        {school.trialExpiresAt && (
                          <div className="school-detail">
                            <span className="detail-label">Expires:</span>
                            <span className="school-date">
                              {new Date(school.trialExpiresAt).toLocaleDateString()} {new Date(school.trialExpiresAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="school-card-footer">
                        <div className="action-buttons">
                          <button
                            className="btn btn-gold btn-sm"
                            onClick={() => openEditForm(school)}
                          >
                            Edit
                          </button>
                          {school.active ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDeactivateSchool(school)}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => openDeleteConfirm(school)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolManagement;